import { useMixpanel } from '@context/MixpanelProvider';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  Alert,
  Box,
  Button,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';
import toast from 'react-hot-toast';

import { BootstrapDialog } from '@components/shared/BootstrapDialog';
import { DYNAMIC_SURVEY_EVENTS } from '@utils/eventSchema';
import SendToParticipantsCard from '../../pages/ai-participants/components/SendToParticipantsCard';
export default function SurveyShare({
  study_link,
  qr_code_url,
  accessCode,
  has_access_code,
  open,
  hideSendToAI,
  handleClose,
  studyType,
  studyId,
  studyTitle,
}) {
  const [openSnackBar, setOpenSnackBar] = React.useState(false);
  const studyID = studyId || study_link?.match(/[?&]study_id=([^&]*)/)?.[1];
  const { trackEvent } = useMixpanel();
  const resolvedStudyType = studyType === 'interview' ? 'interview' : 'survey';

  const handleCopyClick = async (textToCopy) => {
    trackEvent('survey', {
      ...DYNAMIC_SURVEY_EVENTS.CLICKED_COPY_SURVEY_LINK,
    });
    try {
      await navigator.clipboard.writeText(textToCopy);

      toast.success('Link copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleCopyClickAccessCode = async (textToCopy) => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      toast.success('Access code copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleDownloadQR = () => {
    trackEvent('survey', {
      ...DYNAMIC_SURVEY_EVENTS.CLICKED_DOWNLOAD_SURVEY_QRCODE,
    });
  };

  return (
    <>
      <Snackbar
        open={openSnackBar}
        autoHideDuration={1000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackBar(false)} severity="success" sx={{ width: '100%' }}>
          Text copied to clipboard!
        </Alert>
      </Snackbar>
      <BootstrapDialog open={open} onClose={handleClose}>
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}
          onClose={handleClose}
        >
          Share with Participants
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <Divider />
        <DialogContent>
          <Typography variant="subtitle2" gutterBottom>
            {studyTitle || 'Study'}
          </Typography>
          <Typography>
            Share this study via the link, QR code or send it directly to participants.
          </Typography>
          {/* <DialogContentText>Scan me</DialogContentText> */}
          <Box display="flex" flexDirection={'column'} justifyContent="center" alignItems="center">
            <img src={qr_code_url} width="200" height="200" alt="QR Code" />
            <Button
              variant="custom"
              startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: '16px' }} />}
              href={qr_code_url}
              download={'qr_code.png'}
              onClick={handleDownloadQR}
            >
              Download QR
            </Button>
          </Box>
          <Box py={2}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12} display={'flex'} justifyContent={'space-between'} gap={1}>
                <TextField
                  fullWidth
                  disabled
                  variant="outlined"
                  value={study_link?.slice(0, 40) + '...'}
                />
                <Button
                  variant="custom"
                  startIcon={<ContentCopyIcon sx={{ fontSize: '16px' }} />}
                  onClick={() => handleCopyClick(study_link)}
                >
                  Copy
                </Button>
              </Grid>
            </Grid>
          </Box>
          {has_access_code && (
            <Grid item xs={4} mb={2}>
              <Typography variant="body2" gutterBottom>
                Access Code
              </Typography>
              <TextField
                fullWidth
                disabled
                value={accessCode || ''}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <IconButton onClick={() => handleCopyClickAccessCode(accessCode)} size="small">
                      <ContentCopyIcon
                        sx={{
                          fontSize: '18px',
                        }}
                      />
                    </IconButton>
                  ),
                }}
              />
            </Grid>
          )}
          {hideSendToAI || !studyID ? null : (
            <SendToParticipantsCard
              studyId={studyID}
              studyType={resolvedStudyType}
              buttonVariant="contained"
            />
          )}
        </DialogContent>
      </BootstrapDialog>
    </>
  );
}
